/*
 * Acesso VIP (sections/vip-access.liquid).
 *
 *  - envia a senha como termo de busca para a Section Rendering API:
 *    /search?q=<senha>&type=product&section_id=api-vip-products
 *  - sections/api-vip-products.liquid compara a senha no servidor e só devolve os
 *    produtos quando ela confere (data-vip-status="granted")
 *  - lembra a senha no localStorage (data-storage-key) para não pedir de novo;
 *    se a senha do tema mudar, a salva deixa de valer e o formulário volta
 *  - busca a coleção paginada, 12 por vez, pelo botão "Carregar mais" (a inteira de uma vez derruba o
 *    Safari no iPhone) e não entra sozinho de novo se a página caiu montando a grade
 *  - inicializa o quick view dos cards com os mesmos componentes da "featured-collection"
 *  - com o preço VIP ativo, sincroniza o cupom com o carrinho ao liberar e tira ao sair
 */
(function () {
  const API_SECTION_ID = 'api-vip-products';
  const RENDERING_KEY = 'vip-access-rendering';

  class VipAccess extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
      this.onLogout = this.onLogout.bind(this);
      this.onLoadMore = this.onLoadMore.bind(this);
    }

    connectedCallback() {
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.storageKey = this.dataset.storageKey || '';

      this.gate = this.querySelector('[data-vip-gate]');
      this.form = this.querySelector('[data-vip-form]');
      this.input = this.querySelector('[data-vip-input]');
      this.submitButton = this.querySelector('[data-vip-submit]');
      this.message = this.querySelector('[data-vip-message]');
      this.results = this.querySelector('[data-vip-results]');
      this.productsContainer = this.querySelector('[data-vip-products-container]');
      this.grid = this.querySelector('[data-vip-grid]');
      this.empty = this.querySelector('[data-vip-empty]');
      this.logoutButton = this.querySelector('[data-vip-logout]');
      this.moreWrapper = this.querySelector('[data-vip-more-wrapper]');
      this.moreButton = this.querySelector('[data-vip-more]');
      this.errorMessage = this.getTemplateText('[data-vip-error-message]');
      this.requestErrorMessage = this.getTemplateText('[data-vip-request-error]');

      this.form.addEventListener('submit', this.onSubmit);
      this.logoutButton.addEventListener('click', this.onLogout);
      this.moreButton.addEventListener('click', this.onLoadMore);

      const savedPassword = this.getSavedPassword();
      if (savedPassword && !this.crashedWhileRendering()) {
        this.unlock(savedPassword, true);
      }
    }

    disconnectedCallback() {
      this.form.removeEventListener('submit', this.onSubmit);
      this.logoutButton.removeEventListener('click', this.onLogout);
      this.moreButton.removeEventListener('click', this.onLoadMore);
      this.disconnectBatches();
      this.disconnectVideos();
    }

    getTemplateText(selector) {
      const template = this.querySelector(selector);
      return template ? template.innerHTML.trim() : '';
    }

    onSubmit(event) {
      event.preventDefault();

      const password = this.input.value.trim();
      if (!password) {
        this.input.focus();
        return;
      }

      this.unlock(password, false);
    }

    onLogout() {
      this.classList.remove('is-unlocked');
      this.savePassword('');
      this.removeCoupon();
      this.disconnectBatches();
      this.disconnectVideos();
      this.grid.innerHTML = '';
      this.results.hidden = true;
      this.empty.hidden = true;
      this.gate.hidden = false;
      this.input.value = '';
      this.input.focus();
    }

    async unlock(password, silent) {
      this.setLoading(true);
      this.showMessage('');

      try {
        const status = await this.fetchPage(password, 1);

        if (!status) {
          this.savePassword('');
          if (!silent) {
            this.showMessage(this.errorMessage);
            this.input.select();
          }
          return;
        }

        this.password = password;
        this.savePassword(password);
        this.markRendering();
        this.renderProducts(status);
        this.applyCoupon(status.dataset.vipCode);
      } catch (error) {
        console.warn('[vip-access]', error);
        if (!silent) {
          this.showMessage(this.requestErrorMessage);
        }
      } finally {
        this.setLoading(false);
      }
    }

    // Cupom do preço VIP (assets/vip-pricing.js): os preços da listagem já vêm com ele.
    // A senha acabou de ser salva: valida de novo e coloca o cupom se já houver item VIP no carrinho
    applyCoupon(code) {
      this.couponCode = code || '';
      if (this.couponCode && window.VipPricing) {
        window.VipPricing.clearCache();
        window.VipPricing.syncCoupon();
      }
    }

    removeCoupon() {
      if (this.couponCode && window.VipPricing) {
        window.VipPricing.removeCoupon(this.couponCode);
      }
      this.couponCode = '';
    }

    // Uma página da coleção VIP (12 cards, sections/api-vip-products.liquid).
    // Devolve o [data-vip-status] com os cards, ou null se a senha não confere
    async fetchPage(password, page) {
      const html = await this.fetchProducts(password, page);
      const status = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vip-status]');
      if (!status || status.dataset.vipStatus !== 'granted') return null;
      this.prepareVideos(status);
      return status;
    }

    async fetchProducts(password, page) {
      const params = new URLSearchParams({
        q: password,
        type: 'product',
        section_id: API_SECTION_ID,
        page: String(page),
      });

      const response = await fetch(`${this.searchUrl}?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return response.text();
    }

    // A coleção VIP vem paginada: a inteira de uma vez estoura a memória do Safari no iPhone
    // e a página cai ("Um problema ocorreu repetidamente"). "Carregar mais" busca a próxima
    renderProducts(status) {
      this.classList.add('is-unlocked');
      this.gate.hidden = true;
      this.results.hidden = false;
      this.empty.hidden = status.children.length > 0;
      this.disconnectBatches();
      this.disconnectVideos();
      this.grid.innerHTML = '';
      this.appendPage(status);
    }

    async onLoadMore() {
      if (!this.nextPage || this.loadingMore) return;

      this.loadingMore = true;
      this.moreButton.setAttribute('aria-busy', 'true');
      this.moreButton.disabled = true;

      try {
        const status = await this.fetchPage(this.password, this.nextPage);
        if (!status) {
          // A senha do tema mudou com a página aberta: volta para o formulário
          this.onLogout();
          return;
        }
        this.appendPage(status);
      } catch (error) {
        console.warn('[vip-access]', error);
      } finally {
        this.loadingMore = false;
        this.moreButton.setAttribute('aria-busy', 'false');
        this.moreButton.disabled = false;
      }
    }

    appendPage(status) {
      // [data-collection-products] faz o tema reaplicar as animações (AOS) nos cards inseridos
      const fragment = document.createDocumentFragment();
      Array.from(status.children).forEach((card) => fragment.appendChild(document.adoptNode(card)));
      this.grid.appendChild(fragment);
      this.initProductGrid();
      this.observeVideos();

      this.nextPage = parseInt(status.dataset.vipNextPage, 10) || 0;
      this.moreWrapper.hidden = !this.nextPage;
    }

    disconnectBatches() {
      this.nextPage = 0;
      this.moreWrapper.hidden = true;
    }

    // Se a página cair enquanto monta a grade, o Safari recarrega e o login automático com a
    // senha salva derrubaria de novo, em loop. A marca fica na sessão até a grade assentar;
    // achando ela ao carregar, mostra o formulário em vez de entrar sozinho
    markRendering() {
      try {
        window.sessionStorage.setItem(RENDERING_KEY, '1');
      } catch (error) {}
      clearTimeout(this.renderingTimer);
      this.renderingTimer = setTimeout(() => {
        try {
          window.sessionStorage.removeItem(RENDERING_KEY);
        } catch (error) {}
      }, 8000);
    }

    crashedWhileRendering() {
      try {
        const crashed = window.sessionStorage.getItem(RENDERING_KEY) === '1';
        window.sessionStorage.removeItem(RENDERING_KEY);
        return crashed;
      } catch (error) {
        return false;
      }
    }

    // Os cards com vídeo vêm com autoplay + preload="auto" (snippets/product-grid-item.liquid):
    // tira isso antes de inserir; observeVideos() toca só os que estão na tela.
    // O <script> do card não roda quando inserido por JS, então ele também sai.
    prepareVideos(container) {
      container.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.setAttribute('preload', 'none');
        video.setAttribute('playsinline', '');
        video.muted = true;
      });
      container.querySelectorAll('script').forEach((script) => script.remove());
    }

    observeVideos() {
      const videos = this.grid.querySelectorAll('video:not([data-vip-video])');
      if (!videos.length || !('IntersectionObserver' in window)) return;

      this.videoObserver = this.videoObserver || new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target: video, isIntersecting }) => {
            if (isIntersecting) {
              video.muted = true;
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.1 }
      );

      videos.forEach((video) => {
        video.setAttribute('data-vip-video', '');
        video.addEventListener(
          'playing',
          () => {
            setTimeout(() => {
              video.style.opacity = '1';
            }, 150);
          },
          { once: true }
        );
        this.videoObserver.observe(video);
      });
    }

    disconnectVideos() {
      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }
    }

    initProductGrid() {
      // Mesmo ciclo onLoad que o tema roda na seção "featured-collection" (quick view dos cards)
      const sections = window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
      const registration = sections && sections.registered && sections.registered['featured-collection'];
      if (!registration) return;

      const props = {
        id: this.productsContainer.dataset.sectionId,
        type: 'featured-collection',
        container: this.productsContainer,
      };

      registration.getStack().onLoad.forEach((onLoad) => {
        try {
          onLoad.call(props);
        } catch (error) {
          console.warn('[vip-access]', error);
        }
      });
    }

    setLoading(isLoading) {
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.submitButton.disabled = isLoading;
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = !text;
    }

    getSavedPassword() {
      if (!this.storageKey) return '';
      try {
        return window.localStorage.getItem(this.storageKey) || '';
      } catch (error) {
        return '';
      }
    }

    savePassword(password) {
      if (!this.storageKey) return;
      try {
        if (password) {
          window.localStorage.setItem(this.storageKey, password);
        } else {
          window.localStorage.removeItem(this.storageKey);
        }
      } catch (error) {
        // localStorage indisponível (aba anônima, bloqueado): só não lembra a senha
      }
    }
  }

  if (!customElements.get('vip-access')) {
    customElements.define('vip-access', VipAccess);
  }
})();
